import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowDownRight, TrendingUp } from "lucide-react";

const HOLDINGS = [
  { sym: "AAPL", name: "Apple", initials: "AA", price: 228.52, chg: 1.24 },
  { sym: "NVDA", name: "NVIDIA", initials: "NV", price: 121.40, chg: 2.86 },
  { sym: "2222.SR", name: "Saudi Aramco", initials: "AR", price: 28.65, chg: 0.31 },
];

const STRIP = [
  ["QNBK.QA", 0.68], ["META", 0.94], ["BTCUSD", 3.10], ["TSLA", -1.05],
  ["XAUUSD", 0.22], ["AMZN", -0.42], ["ETHUSD", 0.94],
];

const drift = (v, scale) => v + (Math.random() - 0.45) * scale;

const Money = ({ value, digits = 2, className = "" }) => {
  const prev = useRef(value);
  const up = value >= prev.current;
  useEffect(() => { prev.current = value; }, [value]);
  return (
    <span className={`numeric transition-colors duration-500 ${className} ${
      up ? "text-emerald-600" : "text-red-500"}`}>
      {value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}
    </span>
  );
};

/** Balance and prices that actually move — the demo should feel alive, not static. */
export const LivePortfolioScreen = () => {
  const [balance, setBalance] = useState(180650.9);
  const [day, setDay] = useState(4212.65);
  const [rows, setRows] = useState(HOLDINGS);

  useEffect(() => {
    const id = setInterval(() => {
      setBalance((b) => Math.max(1000, drift(b, 260)));
      setDay((d) => drift(d, 90));
      setRows((rs) => rs.map((r) => ({
        ...r, price: Math.max(0.5, drift(r.price, r.price * 0.0035)),
        chg: drift(r.chg, 0.14),
      })));
    }, 1100);
    return () => clearInterval(id);
  }, []);

  const dayPct = (day / Math.max(balance, 1)) * 100;

  return (
    <div className="p-4 pt-2">
      <div className="rounded-2xl bg-[#332654] p-4 text-white">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.14em] text-white/60">Total balance</span>
          <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[color:var(--tharwy-lime)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--tharwy-lime)] live-dot" />LIVE
          </span>
        </div>
        <div className="numeric text-2xl font-bold mt-1 tabular-nums">
          ${balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${
          day >= 0 ? "text-[color:var(--tharwy-lime)]" : "text-red-400"}`}>
          <TrendingUp className="h-3 w-3" />
          {day >= 0 ? "+" : "−"}${Math.abs(day).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {" · "}{day >= 0 ? "+" : "−"}{Math.abs(dayPct).toFixed(2)}% today
        </div>
      </div>

      {/* in-app ticker strip */}
      <div className="marquee mt-2.5 rounded-xl border border-black/5 bg-white py-1.5">
        <div className="marquee-track" style={{ "--marquee-duration": "22s" }}>
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center gap-4 ps-3" aria-hidden={dup === 1}>
              {STRIP.map(([sym, chg]) => (
                <span key={`${dup}-${sym}`} className="flex items-center gap-1 shrink-0 text-[9.5px]">
                  <span className="font-semibold text-slate-600">{sym}</span>
                  <span className={`numeric ${chg >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {chg >= 0 ? "+" : ""}{chg.toFixed(2)}%
                  </span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold">Your positions</span>
        <span className="text-[9.5px] text-[color:var(--tharwy-lime-700)] font-medium">See all</span>
      </div>

      <div className="mt-1.5 space-y-2">
        {rows.map((r) => (
          <div key={r.sym} className="flex items-center gap-2.5 rounded-xl border border-black/5 bg-white px-2.5 py-2">
            <div className="h-7 w-7 rounded-full bg-[#EFF6D8] text-[#3E2E65] flex items-center justify-center text-[10px] font-bold shrink-0">
              {r.initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold leading-none">{r.name}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{r.sym}</div>
            </div>
            <div className="text-end">
              <div className="text-[12px] font-semibold text-[#1A1330]">
                $<Money value={r.price} className="text-[#1A1330]" />
              </div>
              <div className={`numeric text-[10px] inline-flex items-center gap-0.5 ${
                r.chg >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {r.chg >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(r.chg).toFixed(2)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
